import React from 'react';
import Button from '@/components/ui/Button';
import { Code2, Rocket, Users2, Sparkles, ChevronRight, Mail, Phone } from 'lucide-react';
import { COMPANY_INFO } from '@/lib/constants';

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-slate-50 to-slate-100 sm:pt-24 md:pt-16">
      {/* Background pattern with overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
        style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1557683316-973673baf926?q=80&w=2029&auto=format&fit=crop")',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50/80 to-slate-100/60"></div>
      </div>

      {/* Content container */}
      <div className="relative z-10 max-w-3xl mx-auto px-4 py-8 sm:py-12 md:py-16 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-3 sm:mb-4 md:mb-6 text-slate-900">
          Innovez avec{' '}
          <span className="relative">
            <span className="relative z-10 text-orcsys-400">confiance</span>
            <span className="absolute bottom-0 left-0 w-full h-2 bg-orcsys-500/20 -z-10"></span>
          </span>
        </h1>

        <p className="text-base sm:text-lg text-slate-600 mb-6 sm:mb-8 max-w-xl mx-auto">
          Nous créons des logiciels web, mobiles et SaaS pour les entrepreneurs et startups, 
          avec un accent sur le développement rapide et un processus collaboratif transparent.
        </p>

        <div className="flex flex-row items-center justify-center gap-3 sm:gap-4 mb-6 sm:mb-8">
          <a href={`mailto:${COMPANY_INFO.email}`} className="flex items-center gap-2 text-slate-900 hover:text-orcsys-500 transition-colors">
            <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="font-medium text-sm sm:text-base md:text-lg">{COMPANY_INFO.email}</span>
          </a>
          <a href={`tel:${COMPANY_INFO.phone}`} className="flex items-center gap-2 text-slate-900 hover:text-orcsys-500 transition-colors">
            <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
            <span className="font-medium text-sm sm:text-base md:text-lg">{COMPANY_INFO.phone}</span>
          </a>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Button.Link 
            href="#book" 
            size="lg" 
            className="w-full sm:w-auto bg-orcsys-500 hover:bg-orcsys-600 text-white text-sm sm:text-base"
          >
            Prendre rendez-vous
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
          </Button.Link>
          <Button.Link 
            href="#estimation" 
            size="lg" 
            className="w-full sm:w-auto bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300 text-sm sm:text-base"
          >
            Demander une estimation
          </Button.Link>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-4 max-w-xl mx-auto">
          <div className="p-2 sm:p-3 rounded-xl bg-white/80 backdrop-blur-sm shadow-lg">
            <div className="text-xl sm:text-2xl font-bold text-orcsys-400 mb-1">30+</div>
            <div className="text-xs sm:text-sm text-slate-600">Projets livrés</div>
          </div>
          <div className="p-2 sm:p-3 rounded-xl bg-white/80 backdrop-blur-sm shadow-lg">
            <div className="text-xl sm:text-2xl font-bold text-orcsys-400 mb-1">98%</div>
            <div className="text-xs sm:text-sm text-slate-600">Clients satisfaits</div>
          </div>
          <div className="p-2 sm:p-3 rounded-xl bg-white/80 backdrop-blur-sm shadow-lg">
            <div className="text-xl sm:text-2xl font-bold text-orcsys-400 mb-1">-40%</div>
            <div className="text-xs sm:text-sm text-slate-600">par rapport au marché</div>
          </div>
        </div>
      </div>

      {/* Orbiting tech elements */}
      <div className="absolute inset-0 pointer-events-none ">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative w-[550px] sm:w-[550px] md:w-[650px] h-[550px] sm:h-[550px] md:h-[650px]">
            {/* Central circle */}
            <div className="absolute inset-0 rounded-full border-2 border-slate-200 animate-spin-slow"></div>
            
            {/* Orbiting elements */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg flex items-center justify-center border border-slate-200">
                <Code2 className="w-5 h-5 sm:w-6 sm:h-6 text-orcsys-400" />
              </div>
            </div>
            
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg flex items-center justify-center border border-slate-200">
                <Users2 className="w-5 h-5 sm:w-6 sm:h-6 text-orcsys-400" />
              </div>
            </div>
            
            <div className="absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg flex items-center justify-center border border-slate-200">
                <Rocket className="w-5 h-5 sm:w-6 sm:h-6 text-orcsys-400" />
              </div>
            </div>
            
            <div className="absolute right-0 top-1/2 translate-x-1/2 -translate-y-1/2">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg flex items-center justify-center border border-slate-200">
                <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-orcsys-400" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
